import React, { useState, useEffect, useCallback, useRef } from 'react';
import { downloadDatei } from '../lib/download';
import { useDateiDrop } from '../lib/useDateiDrop';
import './AnhangPanel.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const MAX_DATEI_BYTES = 20 * 1024 * 1024; // 20 MB
const ANHANG_ACCEPT = 'image/*,application/pdf,application/vnd.openxmlformats-officedocument.*,text/plain,text/csv,.eml,message/rfc822';

interface Anhang {
  anhang_id: number;
  tenant_id: number;
  entitaet_typ: string;
  entitaet_id: number;
  dateiname: string;
  mime_typ: string | null;
  groesse: number | null;
  hochgeladen_von: number | null;
  erstellt_am: string | null;
}

interface AnhangPanelProps {
  entitaetTyp: string;
  entitaetId: number;
  authToken: string;
  onChange?: () => void;
}

// ── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatDatei(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function mimeIcon(mime: string | null): string {
  if (!mime) return 'DAT';
  if (mime.startsWith('image/')) return 'BLD';
  if (mime === 'application/pdf') return 'PDF';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'XLS';
  if (mime.includes('wordprocessingml') || mime.includes('word')) return 'DOC';
  if (mime.includes('presentationml') || mime.includes('powerpoint')) return 'PPT';
  if (mime.startsWith('text/')) return 'TXT';
  return 'DAT';
}

// ── Komponente ───────────────────────────────────────────────────────────────

const AnhangPanel: React.FC<AnhangPanelProps> = ({ entitaetTyp, entitaetId, authToken, onChange }) => {
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Laden ──
  const ladeListe = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(
        `${API_URL}/api/anhaenge?entitaet_typ=${encodeURIComponent(entitaetTyp)}&entitaet_id=${entitaetId}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (!resp.ok) throw new Error(`Fehler ${resp.status}`);
      setAnhaenge(await resp.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  }, [entitaetTyp, entitaetId, authToken]);

  useEffect(() => {
    ladeListe();
  }, [ladeListe]);

  // ── Upload ──
  const uploadDatei = useCallback(async (datei: File) => {
    setUploadError('');

    // Client-seitige Größenprüfung
    if (datei.size > MAX_DATEI_BYTES) {
      setUploadError('Datei zu groß. Maximum: 20 MB');
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('entitaet_typ', entitaetTyp);
      form.append('entitaet_id', String(entitaetId));
      form.append('datei', datei);

      const resp = await fetch(`${API_URL}/api/anhaenge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: form,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Fehler ${resp.status}`);
      }

      await ladeListe();
      onChange?.();
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  }, [entitaetTyp, entitaetId, authToken, ladeListe, onChange]);

  const handleDateiWahl = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    if (!datei) return;
    await uploadDatei(datei);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Drag-and-Drop ──
  const { dragOver, dropHandlers } = useDateiDrop({
    accept: ANHANG_ACCEPT,
    maxBytes: MAX_DATEI_BYTES,
    onFiles: dateien => uploadDatei(dateien[0]),
    onError: fehler => setUploadError(fehler),
  });

  // ── Download (Blob-basiert über zentralen Helper, damit der Auth-Header mitkommt) ──
  const handleDownload = async (a: Anhang) => {
    setDownloadingId(a.anhang_id);
    try {
      await downloadDatei(`${API_URL}/api/anhaenge/${a.anhang_id}/download`, a.dateiname);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Download fehlgeschlagen');
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Löschen ──
  const handleDelete = async (anhangId: number) => {
    if (!window.confirm('Anhang wirklich löschen?')) return;
    setDeletingId(anhangId);
    try {
      const resp = await fetch(`${API_URL}/api/anhaenge/${anhangId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) throw new Error(`Fehler ${resp.status}`);
      setAnhaenge(prev => prev.filter(a => a.anhang_id !== anhangId));
      onChange?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render ──
  return (
    <div className={`ap-container ${dragOver ? 'ap-container--drag-over' : ''}`} {...dropHandlers}>
      {dragOver && (
        <div className="ap-dropzone-overlay">Datei hier ablegen</div>
      )}
      <div className="ap-header">
        <span className="ap-title">
          Anhänge
          {anhaenge.length > 0 && (
            <span className="ap-badge">{anhaenge.length}</span>
          )}
        </span>
        <label className="ap-upload-btn" aria-label="Datei hochladen">
          {uploading ? (
            <span className="ap-spinner" />
          ) : (
            <>
              <span className="ap-icon">+</span>
              Anhang hinzufügen
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="ap-file-input"
            onChange={handleDateiWahl}
            disabled={uploading}
            accept={ANHANG_ACCEPT}
          />
        </label>
      </div>

      {uploadError && (
        <div className="ap-error ap-error--upload">{uploadError}</div>
      )}
      {error && (
        <div className="ap-error">{error}</div>
      )}

      {loading ? (
        <div className="ap-loading">Lade Anhänge…</div>
      ) : anhaenge.length === 0 ? (
        <div className="ap-empty">Keine Anhänge vorhanden</div>
      ) : (
        <ul className="ap-list">
          {anhaenge.map(a => (
            <li key={a.anhang_id} className="ap-item">
              <span className="ap-item-icon" title={a.mime_typ || ''}>
                {mimeIcon(a.mime_typ)}
              </span>
              <div className="ap-item-info">
                <span className="ap-item-name" title={a.dateiname}>
                  {a.dateiname}
                </span>
                <span className="ap-item-meta">
                  {formatDatei(a.groesse)}
                  {a.erstellt_am && ` · ${formatDatum(a.erstellt_am)}`}
                </span>
              </div>
              <div className="ap-item-actions">
                <button type="button"
                  className="ap-btn ap-btn--download"
                  onClick={() => handleDownload(a)}
                  disabled={downloadingId === a.anhang_id}
                  title="Herunterladen"
                >
                  {downloadingId === a.anhang_id ? (
                    <span className="ap-spinner ap-spinner--sm" />
                  ) : (
                    '↓'
                  )}
                </button>
                <button type="button"
                  className="ap-btn ap-btn--delete"
                  onClick={() => handleDelete(a.anhang_id)}
                  disabled={deletingId === a.anhang_id}
                  title="Löschen"
                >
                  {deletingId === a.anhang_id ? (
                    <span className="ap-spinner ap-spinner--sm" />
                  ) : (
                    '✕'
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AnhangPanel;
